import { useState, useMemo, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  pinReportToDomain,
  pinReportPersonally,
  UserKeys,
} from '../../api/users-api';
import { ReportModel, PinAction } from '../../../types';
import { handleErrors } from '../../utils/errors';
import { Messages } from '../../utils/message-constants';
import { useAnalytics } from '../analytics/create-events';
import * as analytics from '../analytics/analytics.json';
import { ModuleList } from '../analytics/module-list';
import { useUserSettings } from '../../utils/use-user-settings';
import { useStore } from '../../components/state/zustand-store';

interface PersonalPinnedReport {
  id: number;
  pinnedAt: number;
}

export function useReportPinning() {
  const queryClient = useQueryClient();
  const { loggedInUser } = useUserSettings();
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [pinMenuAnchorEl, setPinMenuAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [reportForPinMenu, setReportForPinMenu] = useState<ReportModel | null>(
    null
  );
  const [initialMenuPinnedState, setInitialMenuPinnedState] = useState(false);

  const handlePinMenuClose = useCallback(() => {
    setPinMenuAnchorEl(null);
    setTimeout(() => {
      setReportForPinMenu(null);
    }, 200);
  }, []);

  const allReports = queryClient.getQueryData<ReportModel[]>([
    UserKeys.Reports,
  ]);

  const personalPinnedReports = useMemo(() => {
    if (!allReports || !loggedInUser) return [];

    const pinnedReports: PersonalPinnedReport[] = [];

    allReports.forEach((report) => {
      const pin = report.pinnedPersonally?.find(
        (p) => p.googleId === loggedInUser
      );
      if (pin != null) {
        pinnedReports.push({ id: report.id, pinnedAt: pin.pinnedAt });
      }
    });

    return pinnedReports.sort((a, b) => b.pinnedAt - a.pinnedAt);
  }, [allReports, loggedInUser]);

  const personalPinnedReportIds = useMemo(
    () => personalPinnedReports.map((p) => p.id),
    [personalPinnedReports]
  );

  const { mutate: toggleDomainPin, isPending: isSavingDomainPin } = useMutation(
    {
      mutationFn: (report: ReportModel) => pinReportToDomain(report),
      onSuccess: (updatedReport) => {
        queryClient.setQueryData<ReportModel[] | undefined>(
          [UserKeys.Reports],
          (oldReports) => {
            if (!oldReports) return oldReports;
            return oldReports.map((report) =>
              report.id === updatedReport.id ? updatedReport : report
            );
          }
        );
        const isPinned = updatedReport.domainPinnedAt != null;
        setSnackbar({
          open: true,
          severity: 'success',
          message: isPinned
            ? Messages.Success.Pinned('Report to domain')
            : Messages.Success.Unpinned('Report from domain'),
        });
      },
      onError: (error) => {
        queryClient.invalidateQueries({ queryKey: [UserKeys.Reports] });
        handleErrors({
          error,
          snackbarMessage: Messages.Errors.Network.Pinning(
            'the report to the domain'
          ),
        });
      },
      onSettled: () => {
        handlePinMenuClose();
        createEvent(analytics.report_center.reports.pin_report_to_domain, {
          module: ModuleList.modules.reportCenter.name,
          section: ModuleList.modules.reportCenter.sections.reports,
        });
      },
    }
  );

  const { mutate: togglePersonalPin, isPending: isSavingPersonalPin } =
    useMutation({
      mutationFn: async ({
        reportId,
        action,
      }: {
        reportId: number;
        action: PinAction;
      }) => {
        return pinReportPersonally({ reportId, action });
      },
      onSuccess: (updatedReport, { action }) => {
        queryClient.setQueryData<ReportModel[] | undefined>(
          [UserKeys.Reports],
          (oldReports) => {
            if (!oldReports) return oldReports;
            return oldReports.map((report) =>
              report.id === updatedReport.id ? updatedReport : report
            );
          }
        );
        setSnackbar({
          open: true,
          severity: 'success',
          message:
            action === PinAction.PIN
              ? Messages.Success.Pinned('Report personally')
              : Messages.Success.Unpinned('Report personally'),
        });
      },
      onError: (error) => {
        queryClient.invalidateQueries({ queryKey: [UserKeys.Reports] });
        handleErrors({
          error,
          snackbarMessage: Messages.Errors.Network.Pinning(
            'the report personally'
          ),
        });
      },
      onSettled: () => {
        handlePinMenuClose();
        createEvent(analytics.report_center.reports.pin_report_personally, {
          module: ModuleList.modules.reportCenter.name,
          section: ModuleList.modules.reportCenter.sections.reports,
        });
      },
    });

  const togglePersonalPinWrapper = useCallback(
    (report: ReportModel) => {
      if (!loggedInUser) return;

      const isCurrentlyPinned =
        report.pinnedPersonally?.some((p) => p.googleId === loggedInUser) ??
        false;
      const action = isCurrentlyPinned ? PinAction.UNPIN : PinAction.PIN;

      togglePersonalPin({ reportId: report.id, action });
    },
    [loggedInUser, togglePersonalPin]
  );

  const handlePinMenuClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, report: ReportModel) => {
      setPinMenuAnchorEl(event.currentTarget);
      setReportForPinMenu(report);

      const isPersonallyPinned = personalPinnedReportIds.includes(report.id);
      setInitialMenuPinnedState(
        report.domainPinnedAt != null || isPersonallyPinned
      );
    },
    [personalPinnedReportIds]
  );

  return {
    pinMenuAnchorEl,
    reportForPinMenu,
    initialMenuPinnedState,
    personalPinnedReports,
    personalPinnedReportIds,
    isSavingDomainPin,
    isSavingPersonalPin,
    handlePinMenuClick,
    handlePinMenuClose,
    toggleDomainPin,
    togglePersonalPin: togglePersonalPinWrapper,
  };
}
